'use client';

import { GhostButton } from '@app/app/_components/GhostButton/GhostButton';
import { Heading } from '@app/app/_components/Heading/Heading';
import { CloseOutlinedCircleIcon } from '@app/app/_icons/CloseOutlinedCircleIcon';
import { LifeCalculateResult } from '@app/domain/model/eshop/life/LifeCalculateResult';
import { PaymentSchedule } from '@app/domain/model/eshop/life/PaymentSchedule';
import { formatEuroValue } from '@app/infrastructure/application/formatEuroValue';
import { determineCoverageName } from '@app/infrastructure/policies/determineCoverageName';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';

interface Props {
    paymentSchedule: Nullable<string>;
    calculation: Nullable<LifeCalculateResult>;
    open: boolean;
    onClose: () => void;
}

export function PaymentScheduleDetailsView({ paymentSchedule, calculation, open, onClose }: Props) {
    const { tenant } = useAppParameters();
    const t = useTranslations();

    const scheduleTitle = useMemo(() => {
        if (!paymentSchedule) return '';

        switch (paymentSchedule) {
            case PaymentSchedule.Fixed:
                return t('eshop.life.fixedPayment');
            case PaymentSchedule.Increasing:
                return t('eshop.life.increasingPayment');
            case PaymentSchedule.Changing:
                return t('eshop.life.changingPayment');
            default:
                return '';
        }
    }, [paymentSchedule, t]);

    const scheduleDescriptionKey = useMemo(() => {
        if (!paymentSchedule) return null;

        switch (paymentSchedule) {
            case PaymentSchedule.Fixed:
                return 'eshop.life.fixedPaymentDescription';
            case PaymentSchedule.Increasing:
                return 'eshop.life.increasingPaymentDescription';
            case PaymentSchedule.Changing:
                return 'eshop.life.changingPaymentDescription';
            default:
                return null;
        }
    }, [paymentSchedule]);

    const tableData = useMemo(() => {
        if (!calculation || !calculation.insuredPersons || calculation.insuredPersons.length === 0) {
            return null;
        }

        const premiums = calculation.premiums;
        const insuredPersons = calculation.insuredPersons;
        const fixedPremium = calculation.premium;

        let years: number[] = [];
        if (premiums.length > 0) {
            const premiumsByYear = new Map<number, number>();
            premiums.forEach(premium => {
                premiumsByYear.set(premium.year, premium.totalPremium);
            });
            years = Array.from(premiumsByYear.keys()).sort((a, b) => a - b);
        } else if (insuredPersons.length > 0 && insuredPersons[0].covers.length > 0) {
            const progressTable = insuredPersons[0].covers[0].progressTable;
            years = progressTable.map(item => item.year).sort((a, b) => a - b);
        }

        const premiumsByYear = new Map<number, number>();
        premiums.forEach(premium => {
            premiumsByYear.set(premium.year, premium.totalPremium);
        });

        const coverData: Array<{
            personName: string;
            coverType: string;
            sumInsuredByYear: Map<number, number>;
        }> = [];

        insuredPersons.forEach(person => {
            person.covers.forEach(cover => {
                const sumInsuredByYear = new Map<number, number>();
                cover.progressTable.forEach(item => {
                    sumInsuredByYear.set(item.year, item.sumInsured);
                });

                coverData.push({
                    personName: `${person.name} ${person.surname}`,
                    coverType: cover.coverType,
                    sumInsuredByYear,
                });
            });
        });

        return {
            years,
            premiumsByYear,
            fixedPremium,
            coverData,
            hasMultiplePersons: insuredPersons.length > 1,
            isFixedSchedule: premiums.length === 0,
        };
    }, [calculation]);

    if (!open || !paymentSchedule || !calculation || !tableData) {
        return null;
    }

    return (
        <>
            <button
                type="button"
                className="fixed inset-0 z-30 bg-black opacity-45"
                onClick={onClose}
                aria-label={t('general.close')}
            />

            <div className="fixed inset-0 z-50 overflow-y-auto bg-neutral-50 md:flex md:items-center md:justify-center md:p-8">
                <div className="flex min-h-full w-full flex-col bg-neutral-50 md:min-w-dialog md:max-w-dialog">
                    <div className="mb-4 flex items-start justify-between px-4 py-4">
                        <Heading level="h2" className="flex-1 pt-1">
                            {scheduleTitle}
                        </Heading>
                        <GhostButton onClick={onClose} className="flex-shrink-0">
                            <CloseOutlinedCircleIcon className="size-8" />
                        </GhostButton>
                    </div>

                    {scheduleDescriptionKey && (
                        <div className="mb-6 px-4">
                            <ul className="space-y-2">
                                {t.rich(scheduleDescriptionKey, {
                                    p: children => (
                                        <li className="flex list-disc gap-3">
                                            <span className="mt-2 inline-block size-2 flex-shrink-0 rounded-full bg-primary-500" />
                                            {children}
                                        </li>
                                    ),
                                })}
                            </ul>
                        </div>
                    )}

                    <div className="flex-1 overflow-hidden px-4 pb-6">
                        <div className="h-[60vh] overflow-x-auto overflow-y-auto">
                            <table className="w-full border-collapse">
                                <thead className="sticky top-0 z-10 bg-neutral-50">
                                    <tr className="border-b border-neutral-200">
                                        <th className="w-16 pb-3 pr-4 text-left font-medium">{t('general.year')}</th>
                                        <th className="px-4 pb-3 text-right font-medium">
                                            {t('eshop.life.monthlyPaymentSchedule')}
                                        </th>
                                        {tableData.coverData.map((cover, index) => (
                                            <th key={index} className="px-4 pb-3 text-right font-medium">
                                                {tableData.hasMultiplePersons && (
                                                    <div className="text-xs text-neutral-600">{cover.personName}</div>
                                                )}
                                                <div>{t(determineCoverageName(cover.coverType))}</div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {tableData.years.map(year => (
                                        <tr key={year} className="border-b border-neutral-200 last:border-b-0">
                                            <td className="w-16 py-3 pr-4">{year}</td>
                                            <td className="px-4 py-3 text-right">
                                                {tableData.isFixedSchedule
                                                    ? formatEuroValue(tableData.fixedPremium, tenant)
                                                    : formatEuroValue(tableData.premiumsByYear.get(year) ?? 0, tenant, true)}
                                            </td>
                                            {tableData.coverData.map((cover, index) => (
                                                <td key={index} className="px-4 py-3 text-right">
                                                    {formatEuroValue(cover.sumInsuredByYear.get(year) ?? 0, tenant)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
